import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isAdminUser } from '../../lib/authRoles';
import { useI18n } from '../../i18n';
import {
  LayoutDashboard,
  Building2,
  Users,
  Wrench,
  CalendarDays,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  Menu,
  Shield,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import DomioLogo from '../common/DomioLogo';
import NotificationBell from '../common/NotificationBell';
import LanguageSwitcher from '../common/LanguageSwitcher';
import DisplayCurrencySelect from '../common/DisplayCurrencySelect';
import AccountingNavGroup from './AccountingNavGroup';

export const Sidebar = ({ collapsed, onToggle, onClose }) => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const navItems = [
    { to: '/app/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
    { to: '/app/properties', icon: Building2, labelKey: 'nav.properties' },
    { to: '/app/tenants', icon: Users, labelKey: 'nav.tenants' },
    { accounting: true },
    { to: '/app/maintenance', icon: Wrench, labelKey: 'nav.maintenance' },
    { to: '/app/meetings', icon: CalendarDays, labelKey: 'nav.meetings' },
    { to: '/app/documents', icon: FileText, labelKey: 'nav.documents' },
    ...(isAdminUser(user)
      ? [{ to: '/app/admin', icon: Shield, labelKey: 'nav.admin' }]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-[#071E35] transition-all duration-300 flex flex-col',
        collapsed ? 'w-16' : 'w-64'
      )}
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-[#0A2F52]">
        {!collapsed ? (
          <DomioLogo size="sm" showText textClassName="text-white min-w-0" />
        ) : (
          <DomioLogo size="sm" className="!h-7 w-auto mx-auto" />
        )}
        <div className="flex items-center gap-1">
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-[#0A2F52] lg:hidden"
              data-testid="sidebar-close"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="text-slate-400 hover:text-white hover:bg-[#0A2F52] hidden lg:flex"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <Menu className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.accounting ? 'accounting' : item.to}>
              {item.accounting ? (
                <AccountingNavGroup collapsed={collapsed} />
              ) : (
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-domio-600 text-white'
                        : 'text-slate-400 hover:text-white hover:bg-[#0A2F52]'
                    )
                  }
                  data-testid={`nav-${(item.labelKey || item.label || '').split('.').pop()}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span>{item.label || t(item.labelKey)}</span>}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-[#0A2F52] p-4">
        <div
          className={cn(
            'mb-3 px-1 flex items-center gap-2',
            collapsed ? 'flex-col items-center' : 'justify-between',
          )}
        >
          <div className="flex items-center gap-1.5 min-w-0 shrink-0">
            <DisplayCurrencySelect variant="sidebar" shortLabels compact={collapsed} />
            <LanguageSwitcher variant="dropdown" dark compact={collapsed} />
          </div>
          <NotificationBell />
        </div>

        <NavLink
          to="/app/settings"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors mb-2',
              isActive
                ? 'bg-domio-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-[#0A2F52]'
            )
          }
          data-testid="nav-settings"
        >
          <Settings className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span>{t('nav.settings')}</span>}
        </NavLink>

        {!collapsed && user && (
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-manrope">{t('nav.loggedInAs')}</p>
            <p className="text-sm text-white font-medium truncate">{user.name}</p>
            <p className="text-xs text-slate-400 capitalize">{t(`roles.${user.role}`)}</p>
          </div>
        )}

        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-slate-400 hover:text-white hover:bg-[#0A2F52] transition-colors w-full"
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span>{t('auth.logout')}</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
